import React, { useState } from 'react';
import { Instagram, Twitter, Youtube, Linkedin, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
const privacyContent = `Política de Privacidad — AI Pilot Society

Última actualización: ${new Date().toLocaleDateString('es-ES', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
})}

1. Información que Recopilamos
Recopilamos información personal que usted nos proporciona voluntariamente al registrarse, incluyendo nombre, correo electrónico y datos de pago. También recopilamos datos de uso y navegación de forma automática.

2. Uso de la Información
Utilizamos su información para: procesar transacciones, enviar comunicaciones relacionadas con el servicio, mejorar nuestra plataforma y cumplir con obligaciones legales.

3. Protección de Datos
Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración o destrucción.

4. Cookies y Tecnologías de Seguimiento
Utilizamos cookies y tecnologías similares para mejorar la experiencia del usuario y analizar el tráfico del sitio.

5. Terceros
No vendemos ni compartimos su información personal con terceros, excepto cuando sea necesario para procesar pagos o cumplir con la ley.

6. Sus Derechos
Usted tiene derecho a acceder, rectificar o eliminar sus datos personales en cualquier momento contactándonos directamente.

7. Contacto
Para consultas sobre privacidad, contáctenos a través de nuestros canales oficiales.`;
const termsContent = `Términos y Condiciones — AI Pilot Society

Última actualización: ${new Date().toLocaleDateString('es-ES', {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
})}

1. Aceptación de los Términos
Al acceder y utilizar AI Pilot Society, usted acepta estos términos en su totalidad. Si no está de acuerdo, no utilice este servicio.

2. Naturaleza del Servicio
AI Pilot Society es un programa educativo digital. Todo el contenido es exclusivamente informativo y educativo. NO constituye asesoramiento financiero, de inversión, legal o profesional de ningún tipo.

3. Exención de Responsabilidad sobre Resultados
Los resultados individuales pueden variar significativamente. No garantizamos ingresos, ganancias ni resultados financieros específicos. Los testimonios y ejemplos no representan resultados típicos. El rendimiento pasado no garantiza resultados futuros.

4. Riesgo de Trading e Inversiones
El trading y las inversiones conllevan riesgos significativos, incluyendo la pérdida total del capital invertido. Usted es el único responsable de sus decisiones financieras y de inversión. Andres Castro y AI Pilot Society NO son responsables de ninguna pérdida financiera.

5. Exención de Responsabilidad General
Andres Castro, AI Pilot Society, sus afiliados, empleados y representantes quedan exentos de toda responsabilidad por:
- Pérdidas financieras derivadas de la aplicación del contenido
- Decisiones de inversión o trading tomadas por el usuario
- Resultados comerciales o empresariales del usuario
- Daños directos, indirectos, incidentales o consecuentes

6. Propiedad Intelectual
Todo el contenido del programa está protegido por derechos de autor. Queda prohibida su reproducción, distribución o uso comercial sin autorización expresa.

7. Política de Reembolso
Debido a la naturaleza digital del producto, todas las ventas son finales. No se ofrecen reembolsos una vez se haya accedido al contenido.

8. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del servicio constituye aceptación de los términos modificados.

9. Ley Aplicable
Estos términos se rigen por las leyes aplicables en la jurisdicción correspondiente.

10. Contacto
Para consultas legales, contáctenos a través de nuestros canales oficiales.`;
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}) {
  return <AnimatePresence data-id="element-29">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-30">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative w-full max-w-2xl max-h-[80vh] glass-card rounded-2xl border border-brand-cyan/30 overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-31">
            <div className="flex items-center justify-between p-6 border-b border-white/10" data-id="element-32">
              <h3 className="font-display text-xl font-bold text-white" data-id="element-33">
                {title}
              </h3>
              <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors" data-id="element-34">
                <X className="w-4 h-4" data-id="element-35" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-36">
              <div className="text-white/70 text-sm leading-relaxed whitespace-pre-line" data-id="element-37">
                {content}
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  return <>
      <footer className="bg-black border-t border-white/5 pt-16 pb-8" data-id="element-38">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center" data-id="element-39">
          <div className="flex items-center justify-center mb-8" data-id="element-40">
            <img src="https://cdn.magicpatterns.com/uploads/2FTbScxatEFYg46cgjnota/logo_1.1.png" alt="AI Pilot Society by Andres Castro" className="h-16 w-auto" data-id="element-41" />
          </div>

          <div className="flex gap-6 mb-12" data-id="element-42">
            {[{
            icon: Instagram,
            href: '#'
          }, {
            icon: Twitter,
            href: '#'
          }, {
            icon: Youtube,
            href: '#'
          }, {
            icon: Linkedin,
            href: '#'
          }].map((social, index) => <a key={index} href={social.href} className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-white/60 hover:text-brand-cyan hover:border-brand-cyan/40 transition-all duration-300" data-id="element-43">
                <social.icon className="w-5 h-5" data-id="element-44" />
              </a>)}
          </div>

          <div className="flex gap-6 mb-8" data-id="element-45">
            <button onClick={() => setPrivacyOpen(true)} className="text-white/40 text-sm hover:text-brand-cyan transition-colors duration-300" data-id="element-46">
              Política de Privacidad
            </button>
            <span className="text-white/20" data-id="element-47">|</span>
            <button onClick={() => setTermsOpen(true)} className="text-white/40 text-sm hover:text-brand-cyan transition-colors duration-300" data-id="element-48">
              Términos y Condiciones
            </button>
          </div>

          <div className="w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent mb-8" data-id="element-49" />

          <div className="text-center text-white/40 text-sm" data-id="element-50">
            <p data-id="element-51">
              AI PILOT SOCIETY © {new Date().getFullYear()}. El sistema de
              ingresos del futuro, hoy.
            </p>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Política de Privacidad" content={privacyContent} data-id="element-52" />
      <LegalModal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Términos y Condiciones" content={termsContent} data-id="element-53" />
    </>;
}